import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowLeft, Pencil, Trash2, RotateCcw, X } from "lucide-react";
import { PageHeader }    from "@/components/shared/PageHeader";
import {
  useGasto, useUpdateGasto, useDeleteGasto, useRestoreGasto,
  CategoriaBadge, GastoForm,
} from "@/features/expenses";
import { PAYMENT_METHOD_LABELS, CATEGORY_LABELS, type ExpenseCreate } from "@cold-crew/shared";
import { formatCurrency, formatDate, formatDateTime } from "@/lib/utils";

export const Route = createFileRoute("/_app/gastos/$gastoId")({
  component: GastoDetailPage,
});

function GastoDetailPage() {
  const { gastoId }   = Route.useParams();
  const id            = Number(gastoId);
  const navigate      = useNavigate();

  const { data: gasto, isLoading, isError } = useGasto(id);
  const updateGasto  = useUpdateGasto(id);
  const deleteGasto  = useDeleteGasto();
  const restoreGasto = useRestoreGasto();
  const [editing, setEditing] = useState(false);

  if (isLoading) return (
    <div className="flex items-center justify-center h-64 text-muted-foreground text-sm gap-2">
      <span className="size-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
      Cargando gasto…
    </div>
  );

  if (isError || !gasto) return (
    <div className="flex flex-col items-center justify-center h-64 gap-3">
      <p className="text-sm text-destructive">No se pudo cargar el gasto.</p>
      <Link to="/gastos" className="text-sm text-primary hover:underline">← Volver a gastos</Link>
    </div>
  );

  async function handleUpdate(data: ExpenseCreate) {
    await updateGasto.mutateAsync(data);
    setEditing(false);
  }

  async function handleDelete() {
    if (!confirm("¿Eliminar este gasto?")) return;
    await deleteGasto.mutateAsync(id);
    navigate({ to: "/gastos" });
  }

  const isDeleted = !!gasto.deleted_at;

  if (editing) return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <PageHeader title="Editar gasto" actions={
        <button onClick={() => setEditing(false)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <X className="size-4" /> Cancelar edición
        </button>
      } />
      <div className="rounded-lg border border-border bg-card p-6">
        <GastoForm
          defaultValues={gasto}
          onSubmit={handleUpdate}
          isLoading={updateGasto.isPending}
          submitLabel="Guardar cambios"
        />
      </div>
    </div>
  );

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <PageHeader
        title={CATEGORY_LABELS[gasto.categoria]}
        description={formatDate(gasto.fecha)}
        actions={
          <div className="flex items-center gap-2">
            <Link to="/gastos" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="size-4" /> Volver
            </Link>
            {isDeleted ? (
              <button onClick={() => restoreGasto.mutateAsync(id)} disabled={restoreGasto.isPending}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-50">
                <RotateCcw className="size-3.5" /> Restaurar
              </button>
            ) : (
              <>
                <button onClick={() => setEditing(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-border hover:bg-muted transition-colors">
                  <Pencil className="size-3.5" /> Editar
                </button>
                <button onClick={handleDelete} disabled={deleteGasto.isPending}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-destructive/40 text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50">
                  <Trash2 className="size-3.5" /> Eliminar
                </button>
              </>
            )}
          </div>
        }
      />

      {isDeleted && (
        <div className="rounded-md bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">
          Este gasto fue eliminado el {formatDate(gasto.deleted_at!)}.
        </div>
      )}

      {/* Detail card */}
      <div className="rounded-lg border border-border bg-card divide-y divide-border">
        {/* Main row */}
        <div className="px-5 py-4 flex items-center justify-between">
          <CategoriaBadge categoria={gasto.categoria} />
          <span className="text-2xl font-bold font-display text-foreground tabular-nums">
            {formatCurrency(gasto.monto)}
          </span>
        </div>

        {/* Info grid */}
        <div className="px-5 py-4 grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
          {[
            { label: "Fecha",           value: formatDate(gasto.fecha)                          },
            { label: "Método de pago",  value: PAYMENT_METHOD_LABELS[gasto.metodo_pago]         },
            { label: "Proveedor",       value: gasto.proveedor   ?? "—"                         },
            { label: "Comprobante",     value: gasto.comprobante ?? "—"                         },
            { label: "Registrado",      value: formatDateTime(gasto.created_at)                  },
            { label: "Actualizado",     value: formatDateTime(gasto.updated_at)                  },
          ].map(({ label, value }) => (
            <div key={label}>
              <p className="text-xs text-muted-foreground mb-0.5">{label}</p>
              <p className="font-medium text-foreground">{value}</p>
            </div>
          ))}
        </div>

        {/* Descripción */}
        {gasto.descripcion && (
          <div className="px-5 py-4 text-sm">
            <p className="text-xs text-muted-foreground mb-1">Descripción</p>
            <p className="text-foreground">{gasto.descripcion}</p>
          </div>
        )}

        {/* Notas */}
        {gasto.notas && (
          <div className="px-5 py-4 text-sm">
            <p className="text-xs text-muted-foreground mb-1">Notas</p>
            <p className="text-foreground">{gasto.notas}</p>
          </div>
        )}
      </div>
    </div>
  );
}
